import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Compass, Crown, Star, Flag, Trophy, Medal, ChartBar } from "@phosphor-icons/react";
import api from "../api";

const Section = ({ icon: Icon, title, subtitle, children, testid }) => (
  <div className="ff-panel rounded-md p-5" data-testid={testid}>
    <h2 className="flex items-center gap-2 text-lg font-semibold text-ff-gold"><Icon size={20} weight="duotone" /> {title}</h2>
    {subtitle && <p className="mt-0.5 text-xs text-ff-muted">{subtitle}</p>}
    <div className="mt-3">{children}</div>
  </div>
);

export default function HallOfRenown() {
  const [h, setH] = useState(null);
  const [dispatches, setDispatches] = useState([]);

  useEffect(() => {
    import("../api").then((m) => m.track("hall_view"));
    api.get("/hall-of-renown").then((r) => setH(r.data)).catch(() => setH({}));
    api.get("/hall-dispatches").then((r) => setDispatches(r.data.dispatches || [])).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-ff-bg text-ff-parchment">
      <header className="border-b border-ff-brass/25 bg-ff-bg/90">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <Link to="/" className="flex items-center gap-2">
            <Compass size={26} weight="duotone" className="text-ff-gold animate-compass" />
            <span className="font-display text-lg font-bold text-ff-gold">Aurumward</span>
          </Link>
          <Link to="/login" className="rounded-sm border border-ff-brass/30 px-3 py-1.5 text-sm text-ff-muted hover:border-ff-gold hover:text-ff-gold">Enter</Link>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-10" data-testid="hall-of-renown-page">
        <div className="mb-6 flex items-center gap-2">
          <Trophy size={30} weight="duotone" className="text-ff-gold" />
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">Hall of Renown</h1>
            <p className="text-sm text-ff-muted">The enduring record of Aurumward's most distinguished explorers.</p>
          </div>
        </div>

        {!h ? (
          <div className="ff-panel rounded-md p-8 text-center text-ff-muted">Consulting the archives…</div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2">
            <Section icon={ChartBar} title="Record Holders" subtitle="Highest verified Prestige" testid="hall-record-holders">
              {(h.record_holders || []).length === 0 && <p className="text-sm text-ff-muted">No Prestige records yet.</p>}
              <ol className="space-y-1.5">
                {(h.record_holders || []).map((r, i) => (
                  <li key={i} className="flex items-center justify-between text-sm" data-testid={`hall-record-${i}`}>
                    <span className="text-ff-parchment"><span className="mr-2 text-ff-brass">#{i + 1}</span>{r.name}</span>
                    <span className="text-ff-gold">{r.total}{r.tier ? ` · ${r.tier}` : ""}</span>
                  </li>
                ))}
              </ol>
            </Section>

            <Section icon={Crown} title="Season Leaders" subtitle="Current standings among eligible explorers" testid="hall-season-leaders">
              {(h.current_season_leaders || []).length === 0 && <p className="text-sm text-ff-muted">No eligible explorers yet.</p>}
              <ol className="space-y-1.5">
                {(h.current_season_leaders || []).map((r, i) => (
                  <li key={i} className="flex items-center justify-between text-sm" data-testid={`hall-leader-${i}`}>
                    <span className="text-ff-parchment"><span className="mr-2 text-ff-brass">#{i + 1}</span>{r.name}</span>
                    <span className="text-ff-muted">{r.score} FS{r.rank_name ? ` · ${r.rank_name}` : ""}</span>
                  </li>
                ))}
              </ol>
            </Section>

            <Section icon={Star} title="Singular Title Holders" subtitle="Titles held by only one explorer at a time" testid="hall-singular-holders">
              {(h.singular_title_holders || []).length === 0 && <p className="text-sm text-ff-muted">No singular titles claimed yet.</p>}
              <ul className="space-y-2">
                {(h.singular_title_holders || []).map((s, i) => (
                  <li key={i} className="rounded-sm border border-ff-brass/20 bg-ff-bg/50 p-2.5 text-sm" data-testid={`hall-singular-${i}`}>
                    <div className="flex items-center gap-1.5 font-semibold text-ff-brass"><Star size={13} weight="fill" /> {s.title}</div>
                    <div className="text-ff-parchment">{s.holder}</div>
                  </li>
                ))}
              </ul>
            </Section>

            <Section icon={Flag} title="Historic Firsts" subtitle="Milestones achieved before any other explorer" testid="hall-historic-firsts">
              {(h.historic_firsts || []).length === 0 && <p className="text-sm text-ff-muted">No Historic Firsts recorded yet.</p>}
              <ul className="space-y-2">
                {(h.historic_firsts || []).map((f, i) => (
                  <li key={i} className="border-b border-ff-brass/10 pb-1.5 text-sm last:border-0" data-testid={`hall-first-${i}`}>
                    <div className="text-ff-parchment">{f.description}</div>
                    <div className="text-[11px] text-ff-brass">{f.holder} · {String(f.at).slice(0, 10)}</div>
                  </li>
                ))}
              </ul>
            </Section>

            <Section icon={Medal} title="Past Season Champions" testid="hall-champions">
              {(h.past_season_champions || []).length === 0 && <p className="text-sm text-ff-muted">No seasons have concluded yet.</p>}
              <ul className="space-y-1.5">
                {(h.past_season_champions || []).map((c, i) => (
                  <li key={i} className="flex items-center justify-between text-sm" data-testid={`hall-champion-${i}`}>
                    <span className="text-ff-muted">{c.season}</span>
                    <span className="text-ff-gold">{c.champion || "—"}</span>
                  </li>
                ))}
              </ul>
            </Section>

            <Section icon={Trophy} title="Hall Dispatches" subtitle="Recent renown across the frontier" testid="hall-dispatches-feed">
              {dispatches.length === 0 && <p className="text-sm text-ff-muted">No dispatches yet.</p>}
              <div className="space-y-1.5">
                {dispatches.slice(0, 12).map((d, i) => <p key={i} className="text-sm text-ff-parchment/90" data-testid={`hall-dispatch-${i}`}>◆ {d.text}</p>)}
              </div>
            </Section>
          </div>
        )}
        <p className="mt-8 text-center text-xs text-ff-muted">All records are drawn from server-verified gameplay. Explorers who choose anonymity appear under their chosen alias.</p>
      </main>
    </div>
  );
}
